import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Eye, EyeSlash, PaperPlaneTilt, CheckCircle, ShieldCheck, EnvelopeSimple } from "@phosphor-icons/react";
import logo from "@/assets/logo_h_3d.png";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function handleGoogleRedirect() {
  const nonce = Math.random().toString(36).substring(2);
  sessionStorage.setItem("oauth_nonce", nonce);
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: `${window.location.origin}/auth/callback`,
    response_type: "id_token",
    scope: "openid email profile",
    nonce,
  });
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
}

export default function LoginPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot"
  const [step, setStep] = useState("credentials"); // "credentials" | "otp" | "verify-email"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [phoneRegistrationPending, setPhoneRegistrationPending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpValue, setOtpValue] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("ref")) setMode("register");
    if (searchParams.get("mode") === "forgot") setMode("forgot");
    if (searchParams.get("error")) toast.error("Google sign-in failed. Please try again.");
  }, [searchParams]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown(seconds => Math.max(0, seconds - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { toast.error(mode === "register" ? "Email or phone number and password are required" : "Email or phone number and password are required"); return; }
    if (mode === "register" && !name.trim()) { toast.error("Name is required"); return; }
    if (mode === "register" && password.length < 6) { toast.error("Password must be at least 6 characters"); return; }

    setLoading(true);
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const refCode = searchParams.get("ref") || "";
      const registerByPhone = mode === "register" && !email.includes("@");
      const body = mode === "register"
        ? { ...(registerByPhone ? { phone: email.trim() } : { email: email.trim() }), password, name, ...(refCode && { ref: refCode }) }
        : { email: email.trim(), password };
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || (mode === "register" ? "Registration failed" : "Login failed"));
      }
      const data = await res.json();
      if (data.needs_phone_verification) {
        setPendingEmail(data.phone || email.trim());
        setPhoneRegistrationPending(true);
        setResendCooldown(60);
        setStep("otp");
        toast.success("Verification code sent to your phone");
        return;
      }
      if (data.needs_verification) {
        setPendingEmail(email);
        setPhoneRegistrationPending(false);
        setStep("verify-email");
        return;
      }
      if (data.needs_2fa) {
        setPendingEmail(email);
        setPhoneRegistrationPending(false);
        setStep("otp");
        toast.success("Verification code sent to your email");
        return;
      }
      if (data.session_token) localStorage.setItem('session_token', data.session_token);
      const { session_token, ...user } = data;
      setUser(user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email address"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.detail || "Could not send the link. Please try again shortly.");
      }
      setForgotSent(true);
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpValue.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}${phoneRegistrationPending ? "/auth/verify-phone-registration" : "/auth/verify-otp"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(phoneRegistrationPending
          ? { phone: pendingEmail, code: otpValue }
          : { email: pendingEmail, code: otpValue }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Invalid or expired code");
      }
      const data = await res.json();
      if (data.session_token) localStorage.setItem('session_token', data.session_token);
      const { session_token, ...userData } = data;
      setUser(userData);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.message || "Verification failed");
      setOtpValue("");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    try {
      const endpoint = phoneRegistrationPending ? "/auth/register" : "/auth/login";
      const body = phoneRegistrationPending
        ? { phone: pendingEmail, password, name, ...(searchParams.get("ref") && { ref: searchParams.get("ref") }) }
        : { email: pendingEmail, password };
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to resend");
      setOtpValue("");
      if (phoneRegistrationPending) setResendCooldown(60);
      toast.success(phoneRegistrationPending ? "New code sent to your phone" : "New code sent to your email");
    } catch {
      toast.error("Failed to resend code — try signing in again");
    } finally {
      setResendLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await fetch(`${API}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: pendingEmail }),
      });
      toast.success("Verification email resent — check your inbox");
    } catch {
      toast.error("Failed to resend — try again shortly");
    } finally {
      setResendLoading(false);
    }
  };

  const inputCls = "w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-slate-50 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-sm page-enter">
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8">
          {/* Logo */}
          <div className="text-center mb-6">
            <img src={logo} alt="HostFolio" className="w-16 h-16 object-contain mx-auto mb-2" />
            <h1 className="font-manrope text-2xl font-bold tracking-tight">
              <span className="text-slate-800">Host</span><span className="text-amber-500">Folio</span>
            </h1>
            <p className="mt-1 text-slate-300 text-xs">Your Airbnb Property Dashboard</p>
          </div>

          {/* Email verification pending step */}
          {step === "verify-email" ? (
            <div className="text-center space-y-4 py-2">
              <div className="w-14 h-14 mx-auto bg-amber-50 rounded-full flex items-center justify-center">
                <EnvelopeSimple size={28} className="text-amber-500" weight="duotone" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-800 dark:text-white">Verify your email</h2>
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                  We sent a verification link to<br />
                  <span className="font-semibold text-slate-700 dark:text-slate-200">{pendingEmail}</span>
                </p>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Click the link in the email to activate your account. Check your spam folder if you don't see it.
              </p>
              <div className="space-y-2 pt-1">
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="w-full text-xs text-amber-500 hover:text-amber-600 transition-colors disabled:opacity-50"
                >
                  {resendLoading ? "Sending…" : "Resend verification email"}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("credentials"); setMode("login"); }}
                  className="w-full text-xs text-slate-400 hover:text-slate-600 transition-colors"
                >
                  ← Back to sign in
                </button>
              </div>
            </div>
          ) : step === "otp" ? (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="text-center space-y-1 mb-4">
                <div className="w-12 h-12 mx-auto bg-amber-50 rounded-full flex items-center justify-center mb-3">
                  <ShieldCheck size={24} className="text-amber-500" weight="duotone" />
                </div>
                  <h2 className="text-base font-bold text-slate-800 dark:text-white">{phoneRegistrationPending ? "Check your phone" : "Check your email"}</h2>
                  <p className="text-xs text-slate-500 leading-relaxed">
                  We sent a 6-digit code to your {phoneRegistrationPending ? "phone" : "email"}.<br />
                  <span className="font-semibold text-slate-700 dark:text-slate-200">{pendingEmail}</span>
                </p>
              </div>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otpValue} onChange={setOtpValue} autoFocus>
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <button
                type="submit"
                disabled={loading || otpValue.length !== 6}
                className="w-full bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold rounded-xl py-2.5 text-sm transition-colors shadow-sm"
              >
                {loading ? "Verifying…" : "Verify & Sign In"}
              </button>
              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendLoading || (phoneRegistrationPending && resendCooldown > 0)}
                  className="block w-full text-xs text-amber-500 hover:text-amber-600 transition-colors disabled:opacity-50"
                >
                  {resendLoading ? "Sending…" : phoneRegistrationPending && resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : phoneRegistrationPending ? "Didn't get a text? Resend code" : "Didn't get it? Resend code"}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("credentials"); setOtpValue(""); }}
                  className="block w-full text-xs text-slate-400 hover:text-slate-600 transition-colors"
                >
                  ← Back to sign in
                </button>
              </div>
            </form>
          ) : (
          <>
          {/* Tab toggle — hidden in forgot mode */}
          {mode !== "forgot" && (
            <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-6">
              {[["login", "Sign In"], ["register", "Create Account"]].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setMode(k)}
                  className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition-colors ${mode === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-300 hover:text-slate-700"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Forgot password flow */}
          {mode === "forgot" ? (
            forgotSent ? (
              <div className="text-center space-y-4 py-2">
                <CheckCircle size={48} className="mx-auto text-green-500" weight="duotone" />
                <h2 className="text-base font-bold text-slate-800">Check your email</h2>
                <p className="text-xs text-slate-500 leading-relaxed">
                  If an account exists for <span className="font-semibold text-slate-700">{email}</span>, we've sent a password reset link. It expires in 30 minutes.
                </p>
                <button onClick={() => { setMode("login"); setForgotSent(false); }} className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl py-2.5 text-sm transition-colors">
                  Back to Sign In
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-3">
                <div className="mb-1">
                  <h2 className="text-base font-bold text-slate-800">Set or reset your password</h2>
                  <p className="text-sm text-slate-600 mt-1">Enter your account email. This also works if you joined with Google and have never set a HostFolio password.</p>
                </div>
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className={inputCls}
                  autoComplete="email"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold rounded-xl py-2.5 text-sm transition-colors shadow-sm flex items-center justify-center gap-2"
                >
                  <PaperPlaneTilt size={15} weight="bold" />
                  {loading ? "Sending…" : "Send Password Link"}
                </button>
                <button type="button" onClick={() => setMode("login")} className="w-full text-xs text-slate-400 hover:text-slate-600 transition-colors pt-1">
                  Back to Sign In
                </button>
              </form>
            )
          ) : (
            <>
              {/* Email or phone / password form */}
              <form onSubmit={handleEmailAuth} className="space-y-3">
                {mode === "register" && (
                  <>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className={inputCls}
                    autoComplete="name"
                  />
                  <p className="text-xs text-slate-500">Register with an email address or an Indian mobile number. Phone accounts are verified by SMS.</p>
                  </>
                )}
                <input
                  type={mode === "register" && email.includes("@") ? "email" : "text"}
                  placeholder={mode === "register" ? "Email address or phone number" : "Email address/phone number"}
                  aria-label={mode === "register" ? "Email address or phone number" : "Email address/phone number"}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className={inputCls}
                  autoComplete={mode === "register" && email.includes("@") ? "email" : "username"}
                />
                {mode === "login" && (
                  <p className="text-xs text-slate-500">Team members can sign in with their invited phone number.</p>
                )}
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    placeholder="HostFolio password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className={inputCls + " pr-10"}
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  >
                    {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {mode === "login" && (
                  <div className="text-right">
                    <button type="button" onClick={() => setMode("forgot")} className="text-xs text-amber-500 hover:text-amber-600 transition-colors">
                      Set or reset password
                    </button>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold rounded-xl py-2.5 text-sm transition-colors shadow-sm"
                >
                  {loading ? "Please wait..." : mode === "register" ? "Create Account" : "Sign In"}
                </button>
              </form>

              {mode === "login" && <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                Joined with Google? Use Continue with Google, or select Set or reset password to create a separate HostFolio password for the same account. Your Google password does not work in this form.
              </p>}

              {/* Divider */}
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-slate-100" />
                <span className="text-slate-400 text-xs font-medium">or continue with</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>

              {/* Google OAuth — native redirect, works on all mobile browsers */}
              <div data-testid="google-login-button">
                <button
                  type="button"
                  onClick={handleGoogleRedirect}
                  className="w-full flex items-center justify-center gap-3 bg-white border border-slate-200 rounded-xl py-2.5 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-colors shadow-sm"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>
              </div>

              <p className="mt-5 text-slate-400 text-xs text-center leading-relaxed">
                Your data is private and secured per owner account.
                <br />All values displayed in INR.
              </p>
            </>
          )}
          </>
          )}
        </div>

        <p className="text-center text-slate-400 text-xs mt-5">
          Manage properties &middot; Track revenue &middot; Generate reports
        </p>
      </div>
    </div>
  );
}
